/**
 * HouseholdPreferencesPage — the "Household preferences" surface (T12.2 / 🏠).
 *
 * The context that scales + filters every suggestion. Five groups, each auto-
 * saving through the household-update path (the new household context fields):
 *   - Who's in the household — adults / kids steppers → quantity scaling
 *   - Diet (near-hard filter) — exclusion chips → category exclusion
 *   - Eating style (NEW) — the card-picker → shifts the suggestion mix
 *   - Cooking frequency — the scale → cadence width
 *   - Away from home often? — switch → variance tolerance
 *
 * Diet is the one near-hard filter; everything else is a soft weight. Skipping
 * any field leaves the app working (all nullable on the backend). Persistence
 * rides the existing household-row sync channel.
 */

import {
  useCurrentHousehold,
  useUpdateCurrentHousehold,
} from '@shopista/shared/api/hooks';
import {
  EATING_STYLES,
  COOKING_FREQUENCIES,
  type EatingStyle,
  type CookingFrequency,
} from '@shopista/shared/types';
import { apiErrorMessage } from '@shopista/shared/api';
import { useState } from 'react';
import { Spinner } from '../../components';
import { SettingsLayover } from './SettingsLayover';
import {
  setgroupStyle,
  h4Style,
  hintStyle,
  arrowNoteStyle,
  tagsRowStyle,
  TagToggle,
  StepperRow,
  CardPicker,
  Scale,
  SwitchRow,
  type CardOption,
  type ScaleOption,
} from './prefControls';

// The diet vocabulary shown as exclusion chips. Free-form-tolerant on the
// backend (diet is a jsonb string array); this is the curated starter set.
const DIET_OPTIONS = [
  'Vegetarian',
  'No pork',
  'No beef',
  'Gluten-free',
  'No dairy',
  'Halal',
  'Kosher',
] as const;

const EATING_STYLE_CARDS: CardOption[] = [
  {
    value: 'scratch' satisfies EatingStyle,
    icon: '🍳',
    title: 'Cooks from scratch',
    desc: 'Mostly raw ingredients & staples — we lean toward produce, meat, flour, spices.',
  },
  {
    value: 'both' satisfies EatingStyle,
    icon: '🥗',
    title: 'A bit of both',
    desc: 'Some cooking, some shortcuts — a balanced mix of ingredients and prepared foods.',
  },
  {
    value: 'eats-out' satisfies EatingStyle,
    icon: '🍽️',
    title: 'Eats out often',
    desc: 'Fewer groceries overall — we suggest less, and lean to snacks, drinks & basics.',
  },
  {
    value: 'ready-to-eat' satisfies EatingStyle,
    icon: '🥡',
    title: 'Ready-to-eat & processed',
    desc: 'Heat-and-eat — we promote prepared, frozen & convenience over raw ingredients.',
  },
];

const COOKING_SCALE: ScaleOption[] = COOKING_FREQUENCIES.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

const errorStyle = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
} as const;

export function HouseholdPreferencesPage(): JSX.Element {
  const householdQuery = useCurrentHousehold();
  const update = useUpdateCurrentHousehold();
  const [actionError, setActionError] = useState<string | null>(null);

  const household = householdQuery.data;

  const save = async (patch: Parameters<typeof update.mutateAsync>[0]) => {
    setActionError(null);
    try {
      await update.mutateAsync(patch);
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not save the preference.'));
    }
  };

  const band = {
    variant: 'steers' as const,
    children: (
      <>
        This is the context that <strong>scales</strong> and{' '}
        <strong>filters</strong> every suggestion. Most of it nudges the mix —{' '}
        <strong>diet</strong> is the one that hard-excludes whole categories.
      </>
    ),
  };

  if (householdQuery.isLoading) {
    return (
      <SettingsLayover
        emoji="🏠"
        title="Household preferences"
        backLabel="‹ Menu"
        testId="settings-preferences"
        band={band}
      >
        <Spinner />
      </SettingsLayover>
    );
  }

  const adults = household?.adults ?? 0;
  const kids = household?.kids ?? 0;
  const diet = household?.diet ?? [];
  const eatingStyle = (household?.eating_style ?? null) as EatingStyle | null;
  const cookingFrequency = (household?.cooking_frequency ??
    null) as CookingFrequency | null;
  const travel = household?.travel_flag ?? false;

  const toggleDiet = (value: string) => {
    const next = diet.includes(value)
      ? diet.filter((d) => d !== value)
      : [...diet, value];
    void save({ diet: next });
  };

  return (
    <SettingsLayover
      emoji="🏠"
      title="Household preferences"
      backLabel="‹ Menu"
      testId="settings-preferences"
      band={band}
    >
      {actionError && (
        <div role="alert" style={errorStyle} data-testid="settings-preferences-error">
          {actionError}
        </div>
      )}

      {/* Composition */}
      <section style={setgroupStyle} data-testid="settings-pref-composition">
        <h4 style={h4Style}>Who&apos;s in the household</h4>
        <p style={hintStyle}>
          Sizes our quantity suggestions — a family of four needs more than a
          couple.
        </p>
        <StepperRow
          title="Adults"
          value={adults}
          onStep={(d) => void save({ adults: Math.max(0, adults + d) })}
          testId="settings-pref-adults"
        />
        <StepperRow
          title="Kids"
          desc="Ages set per-member in Account & members"
          value={kids}
          onStep={(d) => void save({ kids: Math.max(0, kids + d) })}
          testId="settings-pref-kids"
        />
        <p style={arrowNoteStyle}>
          → <strong>Quantity scaling.</strong> Bigger household → larger suggested
          quantities.
        </p>
      </section>

      {/* Diet — the near-hard filter */}
      <section style={setgroupStyle} data-testid="settings-pref-diet">
        <h4 style={h4Style}>
          Diet{' '}
          <span style={{ fontWeight: 400, color: 'var(--color-status-danger)', fontSize: 'var(--font-size-xs)' }}>
            · near-hard filter
          </span>
        </h4>
        <p style={hintStyle}>
          Household-wide diet. Unlike the controls below, diet{' '}
          <strong>excludes whole categories</strong> — vegetarian means meat
          suggestions stop, period. Tap to toggle.
        </p>
        <div style={tagsRowStyle}>
          {DIET_OPTIONS.map((opt) => (
            <TagToggle
              key={opt}
              label={opt}
              on={diet.includes(opt)}
              onToggle={() => toggleDiet(opt)}
              testId={`settings-pref-diet-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            />
          ))}
        </div>
        <p style={arrowNoteStyle}>
          → <strong>Category exclusion.</strong> Matching categories are dropped
          from suggestions.
        </p>
      </section>

      {/* Eating style — the NEW card picker */}
      <section style={setgroupStyle} data-testid="settings-pref-eating-style">
        <h4 style={h4Style}>
          Eating style{' '}
          <span
            style={{
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-accent-primary)',
              fontSize: 'var(--font-size-xs)',
              backgroundColor: 'var(--color-accent-subtle)',
              padding: '0.125rem var(--space-2)',
              borderRadius: 'var(--radius-pill)',
            }}
          >
            NEW
          </span>
        </h4>
        <p style={hintStyle}>
          How your food usually arrives — raw ingredients you cook, or ready-to-eat
          you reheat. Different from diet (what you won&apos;t eat) and from how
          often you cook.
        </p>
        <CardPicker
          options={EATING_STYLE_CARDS}
          value={eatingStyle}
          onPick={(value) => void save({ eatingStyle: value })}
          testId="settings-pref-eating-style-picker"
        />
        <p style={arrowNoteStyle}>
          → <strong>Shifts the suggestion mix.</strong> Scratch promotes raw
          ingredients; ready-to-eat promotes prepared/frozen/heat-and-eat.
        </p>
      </section>

      {/* Cooking frequency */}
      <section style={setgroupStyle} data-testid="settings-pref-cooking">
        <h4 style={h4Style}>Cooking frequency</h4>
        <p style={hintStyle}>
          How often you cook at home — sets how tightly we time restock reminders.
        </p>
        <Scale
          options={COOKING_SCALE}
          value={cookingFrequency}
          onPick={(value) => void save({ cookingFrequency: value })}
          testId="settings-pref-cooking-scale"
        />
        <p style={arrowNoteStyle}>
          → <strong>Cadence width.</strong> Cook often → tighter restock intervals;
          rarely → wider.
        </p>
      </section>

      {/* Travel */}
      <section style={setgroupStyle} data-testid="settings-pref-travel">
        <h4 style={h4Style}>Away from home often?</h4>
        <SwitchRow
          title="We travel or eat away a lot"
          desc="Shifts, travel, kids at grandparents — your shopping rhythm is irregular by design."
          on={travel}
          onToggle={() => void save({ travelFlag: !travel })}
          testId="settings-pref-travel-switch"
        />
        <p style={arrowNoteStyle}>
          → <strong>Variance tolerance.</strong> We won&apos;t drop confidence just
          because your intervals are uneven.
        </p>
      </section>

      {/* Reference EATING_STYLES so a vocabulary drift surfaces at build. */}
      <span hidden data-eating-styles={EATING_STYLES.join(',')} />
    </SettingsLayover>
  );
}
